import { useMemo, useState, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ImageOff, MoreHorizontal, Pencil, Plus, Search, Tags, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { DataTablePagination } from '@/components/common/data-table-pagination'
import { getErrorMessage } from '@/lib/api'
import { withServiceInstanceId } from '@/lib/service-instance'
import { useStoreOrgScope } from '@/hooks/use-store-org-scope'
import storeCommerceApi from '@/services/store-commerce'
import { STORE_TAG_ITEM_TYPES, type StoreTagItemType, type StoreTagset, type StoreTagsetPayload } from '@/types'

type TagsetForm = {
  id?: string | number
  name: string
  code: string
  max_tags: string
  item_types: string
  active: string
  logo: File | null
  icon: File | null
  sample_view: File | null
}

const itemTypeOptions: Array<{ value: string; label: string }> = [
  { value: String(STORE_TAG_ITEM_TYPES.all), label: 'All' },
  { value: String(STORE_TAG_ITEM_TYPES.product), label: 'Product' },
  { value: String(STORE_TAG_ITEM_TYPES.category), label: 'Category' },
  { value: String(STORE_TAG_ITEM_TYPES.productVariant), label: 'Product Variant' },
  { value: String(STORE_TAG_ITEM_TYPES.offer), label: 'Offer' },
  { value: String(STORE_TAG_ITEM_TYPES.store), label: 'Store' },
]

const emptyForm: TagsetForm = {
  name: '',
  code: '',
  max_tags: '100',
  item_types: String(STORE_TAG_ITEM_TYPES.product),
  active: '1',
  logo: null,
  icon: null,
  sample_view: null,
}

const TAGSET_CODE_MAX_LENGTH = 32

function slugify(str: string) {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/--+/g, '-')
    .slice(0, TAGSET_CODE_MAX_LENGTH)
}

const getItemTypeLabel = (value?: string | number | null) => {
  const option = itemTypeOptions.find((item) => item.value === String(value ?? ''))
  return option?.label ?? 'All'
}

const getItemTypeBadgeClass = (value?: string | number | null) => {
  const normalized = Number(value)
  if (normalized === STORE_TAG_ITEM_TYPES.product) return 'bg-blue-100 text-blue-800 border-blue-200'
  if (normalized === STORE_TAG_ITEM_TYPES.category) return 'bg-amber-100 text-amber-800 border-amber-200'
  if (normalized === STORE_TAG_ITEM_TYPES.productVariant) return 'bg-violet-100 text-violet-800 border-violet-200'
  if (normalized === STORE_TAG_ITEM_TYPES.offer) return 'bg-emerald-100 text-emerald-800 border-emerald-200'
  if (normalized === STORE_TAG_ITEM_TYPES.store) return 'bg-rose-100 text-rose-800 border-rose-200'
  return 'bg-slate-100 text-slate-800 border-slate-200'
}

const getScopeLabel = () => 'Organization'

const getScopeBadgeClass = () => 'bg-emerald-100 text-emerald-800 border-emerald-200'

const isActive = (value?: number | boolean | null) => !(value === false || value === 0)

const imageCell = (src?: string | null, alt = 'Image') => {
  if (!src) {
    return <div className="flex h-10 w-10 items-center justify-center rounded-md border bg-muted text-muted-foreground"><ImageOff className="h-4 w-4" /></div>
  }

  return <img src={src} alt={alt} className="h-10 w-10 rounded-md border object-cover" />
}

export default function CommerceTagsetsPage() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { serviceInstanceId, orgInstanceId, isLoading: scopeLoading } = useStoreOrgScope()
  const [search, setSearch] = useState('')
  const [activeFilter, setActiveFilter] = useState(searchParams.get('active') ?? '1')
  const [itemTypeFilter, setItemTypeFilter] = useState(searchParams.get('tag_id') ?? 'all')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<StoreTagset | null>(null)
  const [form, setForm] = useState<TagsetForm>(emptyForm)
  // Track if user has manually edited the code field
  const codeManuallyEdited = useRef(false)
  // Track the last auto-generated slug for comparison
  const lastAutoSlug = useRef('')

  const tagsetsQuery = useQuery({
    queryKey: ['store-tagsets', serviceInstanceId, activeFilter, itemTypeFilter],
    queryFn: () => itemTypeFilter !== 'all'
      ? storeCommerceApi.listTagsetsForItemType(Number(itemTypeFilter) as StoreTagItemType, { active: activeFilter })
      : storeCommerceApi.listTagsets({
        page: 1,
        page_size: 500,
        active: activeFilter,
      }),
    enabled: Boolean(serviceInstanceId),
    staleTime: 30_000,
  })

  const tagsets = tagsetsQuery.data?.results ?? []
  const filteredTagsets = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return tagsets
    return tagsets.filter((tagset) => [tagset.name, tagset.code, getItemTypeLabel(tagset.item_types), getScopeLabel()].some((value) => String(value ?? '').toLowerCase().includes(term)))
  }, [search, tagsets])

  const effectivePageSize = Number(pageSize) > 0 ? Number(pageSize) : 10

  const pageRows = useMemo(() => {
    const start = (page - 1) * effectivePageSize
    return filteredTagsets.slice(start, start + effectivePageSize)
  }, [filteredTagsets, page, effectivePageSize])

  const invalidateTagsets = () => queryClient.invalidateQueries({ queryKey: ['store-tagsets'] })

  const createMutation = useMutation({
    mutationFn: (payload: StoreTagsetPayload) => storeCommerceApi.createTagset(payload),
    onSuccess: () => {
      invalidateTagsets()
      toast.success('Tagset created')
      setDialogOpen(false)
      setForm(emptyForm)
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string | number; payload: Partial<StoreTagsetPayload> }) => storeCommerceApi.updateTagset(id, payload),
    onSuccess: () => {
      invalidateTagsets()
      toast.success('Tagset updated')
      setDialogOpen(false)
      setForm(emptyForm)
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string | number) => storeCommerceApi.deleteTagset(id),
    onSuccess: () => {
      invalidateTagsets()
      toast.success('Tagset removed')
      setDeleteTarget(null)
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const openCreate = () => {
    setForm({
      ...emptyForm,
      item_types: itemTypeFilter !== 'all' ? itemTypeFilter : String(STORE_TAG_ITEM_TYPES.product),
    })
    codeManuallyEdited.current = false
    lastAutoSlug.current = ''
    setDialogOpen(true)
  }

  const openEdit = (tagset: StoreTagset) => {
    setForm({
      id: tagset.id,
      name: tagset.name ?? '',
      code: tagset.code ?? '',
      max_tags: String(tagset.max_tags ?? '100'),
      item_types: String(tagset.item_types ?? STORE_TAG_ITEM_TYPES.product),
      active: isActive(tagset.active) ? '1' : '0',
      logo: null,
      icon: null,
      sample_view: null,
    })
    codeManuallyEdited.current = false
    lastAutoSlug.current = tagset.code ?? ''
    setDialogOpen(true)
  }

  const submitForm = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!form.name.trim() || !form.code.trim() || !form.max_tags.trim()) {
      toast.error('Name, code, and max tags are required')
      return
    }

    const payload: StoreTagsetPayload = {
      name: form.name.trim(),
      code: form.code.trim().slice(0, TAGSET_CODE_MAX_LENGTH),
      max_tags: form.max_tags.trim(),
      item_types: Number(form.item_types) as StoreTagItemType,
      active: Number(form.active),
      org_instance: orgInstanceId ?? undefined,
      logo: form.logo,
      icon: form.icon,
      sample_view: form.sample_view,
      scope: 'organization',
    }

    if (form.id) {
      const editPayload: Partial<StoreTagsetPayload> = { ...payload }
      delete editPayload.org_instance
      delete editPayload.item_types
      delete editPayload.scope
      updateMutation.mutate({ id: form.id, payload: editPayload })
    } else {
      createMutation.mutate(payload)
    }
  }

  const openTags = (tagset: StoreTagset) => {
    navigate(withServiceInstanceId(`/services/tagsets/${tagset.id}/tags`, serviceInstanceId))
  }

  return (
    <div className="space-y-6">
      <section className="grid gap-4 lg:grid-cols-3">
        <Card><CardContent className="flex items-center gap-4 p-5"><div className="flex h-12 w-12 items-center justify-center rounded-md bg-sky-100 text-sky-700"><Tags className="h-5 w-5" /></div><div><p className="text-sm text-muted-foreground">Tagsets</p><p className="text-2xl font-semibold">{tagsets.length}</p></div></CardContent></Card>
        <Card><CardContent className="flex items-center gap-4 p-5"><div className="flex h-12 w-12 items-center justify-center rounded-md bg-emerald-100 text-emerald-700"><Tags className="h-5 w-5" /></div><div><p className="text-sm text-muted-foreground">Active</p><p className="text-2xl font-semibold">{tagsets.filter((item) => isActive(item.active)).length}</p></div></CardContent></Card>
        <Card><CardContent className="flex items-center gap-4 p-5"><div className="flex h-12 w-12 items-center justify-center rounded-md bg-amber-100 text-amber-700"><Tags className="h-5 w-5" /></div><div><p className="text-sm text-muted-foreground">Filtered</p><p className="text-2xl font-semibold">{filteredTagsets.length}</p></div></CardContent></Card>
      </section>

      <Card>
        <CardHeader className="border-b border-border/60">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <CardTitle>Tagsets</CardTitle>
              <p className="text-sm text-muted-foreground">Manage tag catalogs and open a tagset to maintain its tags.</p>
            </div>
            <Button onClick={openCreate}><Plus className="mr-2 h-4 w-4" />New</Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 p-4 sm:p-6">
          <div className="grid gap-3 lg:grid-cols-[1fr_180px_220px]">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input className="pl-9" placeholder="Search tagsets" value={search} onChange={(event) => { setSearch(event.target.value); setPage(1) }} />
            </div>
            <Select value={activeFilter} onValueChange={(value) => { setActiveFilter(value); setPage(1) }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="-1">View all</SelectItem>
                <SelectItem value="1">Active</SelectItem>
                <SelectItem value="0">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Select value={itemTypeFilter} onValueChange={(value) => { setItemTypeFilter(value); setPage(1) }}>
              <SelectTrigger><SelectValue placeholder="Item type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All item types</SelectItem>
                {itemTypeOptions.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Max Tags</TableHead>
                  <TableHead>Media</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {scopeLoading || tagsetsQuery.isLoading ? (
                  <TableRow><TableCell colSpan={7} className="py-10 text-center text-muted-foreground">Loading tagsets...</TableCell></TableRow>
                ) : tagsetsQuery.isError ? (
                  <TableRow><TableCell colSpan={7} className="py-10 text-center text-destructive">{getErrorMessage(tagsetsQuery.error)}</TableCell></TableRow>
                ) : pageRows.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="py-10 text-center text-muted-foreground">No tagsets found.</TableCell></TableRow>
                ) : pageRows.map((tagset) => (
                  <TableRow key={String(tagset.id)}>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{tagset.name}</span>
                        <Badge variant="outline" className={getScopeBadgeClass()}>{getScopeLabel()}</Badge>
                      </div>
                    </TableCell>
                    <TableCell>{tagset.code}</TableCell>
                    <TableCell><Badge variant="outline" className={getItemTypeBadgeClass(tagset.item_types)}>{getItemTypeLabel(tagset.item_types)}</Badge></TableCell>
                    <TableCell>{tagset.max_tags ?? '-'}</TableCell>
                    <TableCell><div className="flex gap-2">{imageCell(tagset.logo, 'Logo')}{imageCell(tagset.icon, 'Icon')}{imageCell(tagset.sample_view, 'Sample')}</div></TableCell>
                    <TableCell><Badge variant={isActive(tagset.active) ? 'default' : 'secondary'}>{isActive(tagset.active) ? 'Active' : 'Inactive'}</Badge></TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openTags(tagset)}><Tags className="mr-2 h-4 w-4" />Tags</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openEdit(tagset)}><Pencil className="mr-2 h-4 w-4" />Edit</DropdownMenuItem>
                          <DropdownMenuItem className="text-destructive" onClick={() => setDeleteTarget(tagset)}><Trash2 className="mr-2 h-4 w-4" />Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <DataTablePagination page={page} pageSize={effectivePageSize} totalCount={filteredTagsets.length} onPageChange={setPage} onPageSizeChange={(value) => { setPageSize(Number(value) || 10); setPage(1) }} />
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <form onSubmit={submitForm} className="space-y-4">
            <DialogHeader><DialogTitle>{form.id ? 'Edit Tagset' : 'Create Tagset'}</DialogTitle></DialogHeader>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={form.name}
                  onChange={(event) => {
                    const newName = event.target.value
                    setForm((current) => {
                      // Only auto-update code if not manually edited or if code matches last auto-slug
                      if (!codeManuallyEdited.current || current.code === lastAutoSlug.current) {
                        const autoSlug = slugify(newName)
                        lastAutoSlug.current = autoSlug
                        return { ...current, name: newName, code: autoSlug }
                      }
                      return { ...current, name: newName }
                    })
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label>Code</Label>
                <Input
                  value={form.code}
                  maxLength={TAGSET_CODE_MAX_LENGTH}
                  onChange={(event) => {
                    const newCode = event.target.value.slice(0, TAGSET_CODE_MAX_LENGTH)
                    setForm((current) => ({ ...current, code: newCode }))
                    // If user clears the code, allow auto-population again
                    if (newCode === '' || newCode === slugify(form.name)) {
                      codeManuallyEdited.current = false
                    } else {
                      codeManuallyEdited.current = true
                    }
                    lastAutoSlug.current = slugify(form.name)
                  }}
                  placeholder="Auto-generated from name"
                />
                <p className="text-xs text-muted-foreground">Up to {TAGSET_CODE_MAX_LENGTH} characters.</p>
              </div>
              <div className="space-y-2"><Label>Max Tags</Label><Input type="number" min="1" value={form.max_tags} onChange={(event) => setForm((current) => ({ ...current, max_tags: event.target.value }))} /></div>
              <div className="space-y-2"><Label>Item Type</Label><Select value={form.item_types} disabled={Boolean(form.id)} onValueChange={(value) => setForm((current) => ({ ...current, item_types: value }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{itemTypeOptions.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent></Select></div>
              {form.id ? <div className="space-y-2"><Label>Status</Label><Select value={form.active} onValueChange={(value) => setForm((current) => ({ ...current, active: value }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="1">Active</SelectItem><SelectItem value="0">Inactive</SelectItem></SelectContent></Select></div> : null}
              <div className="space-y-2"><Label>Logo</Label><Input type="file" accept="image/*" onChange={(event) => setForm((current) => ({ ...current, logo: event.target.files?.[0] ?? null }))} /></div>
              <div className="space-y-2"><Label>Icon</Label><Input type="file" accept="image/*" onChange={(event) => setForm((current) => ({ ...current, icon: event.target.files?.[0] ?? null }))} /></div>
              <div className="space-y-2 sm:col-span-2"><Label>Sample View</Label><Input type="file" accept="image/*" onChange={(event) => setForm((current) => ({ ...current, sample_view: event.target.files?.[0] ?? null }))} /></div>
            </div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button><Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>{form.id ? 'Update' : 'Create'}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete Tagset</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Delete {deleteTarget?.name}? The backend will block this if active tags still reference the tagset.</p>
          <DialogFooter><Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button><Button variant="destructive" onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)} disabled={deleteMutation.isPending}>Delete</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
